import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { useSection3AnalyticsSummary, useSection3Health } from './section3Api';
import { Section3StateView } from './Section3StateView';
import { formatMinor } from './section5Format';

/** 24.5 — Section 3 sales-intelligence analytics. BDE sees self scope; team/all
 *  selectors appear only when the server authorizes them (canSelectScope). */
export function Section3AnalyticsPanel({ enabled = true }: { enabled?: boolean }) {
  const health = useSection3Health();
  const q = useSection3AnalyticsSummary(enabled);
  const d = q.data;

  return (
    <Card>
      <CardHeader
        title="Sales intelligence (Section 3)"
        subtitle="Governed Section 3 records only — loss, win-rate, first-response and pipeline KPIs are server-scoped and currency-partitioned. Legacy CRM deals remain in the Deal Pipeline panel above."
        action={health.data ? <Badge tone="info">Scope: {health.data.scope}</Badge> : null}
      />
      <CardBody>
        <Section3StateView
          isLoading={q.isLoading}
          isError={q.isError}
          error={q.error}
          isFetching={q.isFetching}
          prereqTitle="Sales intelligence unavailable"
          prereqDescription="No sales-intelligence summary is available for your current scope. The data may be outside your access or not available yet."
        >
          {d && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-card border border-outline p-4">
                <h4 className="text-data-md font-semibold text-on-surface">Win / loss</h4>
                <p className="mt-2 text-body-sm text-on-surface-variant">Win rate: {d.winPercentage == null ? 'No data' : `${d.winPercentage.toFixed(1)}%`}</p>
                <p className="text-body-sm text-on-surface-variant">Won / lost: {d.wonLostRatioText || 'No data'}</p>
                <p className="text-body-sm text-on-surface-variant">Overdue follow-ups: {d.overdueFollowUpCount}</p>
              </div>
              <div className="rounded-card border border-outline p-4">
                <h4 className="text-data-md font-semibold text-on-surface">Lost value by currency</h4>
                {d.lostValueByCurrency?.length === 0 ? (
                  <p className="mt-2 text-body-sm text-on-surface-variant">No losses in range.</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {d.lostValueByCurrency?.map((r) => (
                      <li key={r.currency} className="text-body-sm text-on-surface-variant">{formatMinor(r.currency, r.amountMinor)}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-card border border-outline p-4">
                <h4 className="text-data-md font-semibold text-on-surface">Open pipeline by currency</h4>
                {d.openPipelineByCurrency?.length === 0 ? (
                  <p className="mt-2 text-body-sm text-on-surface-variant">Empty pipeline.</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {d.openPipelineByCurrency?.map((r) => (
                      <li key={r.currency} className="text-body-sm text-on-surface-variant">{formatMinor(r.currency, r.amountMinor)}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-card border border-outline p-4">
                <h4 className="text-data-md font-semibold text-on-surface">Loss reasons</h4>
                {d.lossCountByReason?.length === 0 ? (
                  <p className="mt-2 text-body-sm text-on-surface-variant">No losses recorded.</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {d.lossCountByReason?.map((r) => (
                      <li key={r.reasonCode} className="text-body-sm text-on-surface-variant">{r.reasonCode}: {r.count}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </Section3StateView>
      </CardBody>
    </Card>
  );
}
